/* ── City & Service data for SEO landing pages ── */

export type ServiceCategory = {
  slug: string;
  label: string;
  shortLabel: string;
  description: string;
  heroLine: string;
  features: string[];
  cta: { label: string; href: string };
};

export type LocationData = {
  slug: string;
  name: string;
  type: "city" | "unincorporated" | "neighborhood";
};

export type CountyData = {
  name: string;
  cityCount: number;
  locations: LocationData[];
};

/* ── Service categories ── */
export const SERVICE_CATEGORIES: ServiceCategory[] = [
  {
    slug: "roofing",
    label: "Roofing Services",
    shortLabel: "Roofing",
    description: "Full roof replacements, repairs, tile resets, and inspections by licensed roofing contractors.",
    heroLine: "Licensed Roofing Contractors",
    features: [
      "Shingle, tile, metal & flat roof installation",
      "Roof repairs & emergency leak service",
      "Tile resets & underlayment replacement",
      "10-year roofing labor warranty",
      "All permits pulled & inspections handled",
      "Free on-site roof inspections",
    ],
    cta: { label: "Get a Free Roofing Estimate", href: "/get-quote" },
  },
  {
    slug: "solar",
    label: "Solar Installation",
    shortLabel: "Solar",
    description: "Residential and commercial solar panel installation with 25-year workmanship warranty.",
    heroLine: "Expert Solar Installers",
    features: [
      "Custom solar system design & engineering",
      "25-year workmanship warranty",
      "10-year roof penetration warranty",
      "Battery storage integration",
      "Net metering & utility coordination",
      "All permits & interconnection handled",
    ],
    cta: { label: "Get a Free Solar Quote", href: "/get-quote" },
  },
  {
    slug: "battery-storage",
    label: "Battery Storage",
    shortLabel: "Battery",
    description: "Tesla Powerwall and Enphase battery installation for energy independence and backup power.",
    heroLine: "Home Battery Installation",
    features: [
      "Tesla Powerwall certified installer",
      "Enphase IQ battery systems",
      "Whole-home backup power",
      "10-year Tesla manufacturer warranty",
      "25-year Enphase microinverter warranty",
      "Smart energy management integration",
    ],
    cta: { label: "Get a Battery Quote", href: "/get-quote" },
  },
  {
    slug: "electrical",
    label: "Electrical Services",
    shortLabel: "Electrical",
    description: "Licensed electricians for panel upgrades, rewiring, EV charger installation, and smart home systems.",
    heroLine: "Licensed Electrical Contractors",
    features: [
      "Main panel upgrades (100A–400A)",
      "EV charger installation (Level 2 & commercial)",
      "Smart home wiring & automation",
      "Dedicated circuits & sub-panels",
      "Whole-home rewiring",
      "Code compliance & safety inspections",
    ],
    cta: { label: "Get an Electrical Quote", href: "/get-quote" },
  },
  {
    slug: "ev-chargers",
    label: "EV Charger Installation",
    shortLabel: "EV Chargers",
    description: "Home and commercial EV charger installation by licensed electricians. Level 2 and DC fast charging.",
    heroLine: "EV Charger Installation",
    features: [
      "Level 2 home charger installation",
      "Commercial EV charging stations",
      "Panel upgrade if needed",
      "All major charger brands supported",
      "Permit & inspection handling",
      "Smart charging & load management",
    ],
    cta: { label: "Get an EV Charger Quote", href: "/get-quote" },
  },
  {
    slug: "wildfire-protection",
    label: "Wildfire Protection",
    shortLabel: "Wildfire",
    description: "Wildfire defense sprinkler systems — roof saturation, perimeter protection, and smart activation for high fire-risk properties.",
    heroLine: "Wildfire Defense Systems",
    features: [
      "Roof-mounted sprinkler systems for ember suppression",
      "Perimeter ground sprinklers for defensible space",
      "Smart / automatic sensor-based activation",
      "Solar + battery backup power for pumps",
      "Compatible with tile, composition, metal & flat roofs",
      "Free fire risk assessment & system design",
    ],
    cta: { label: "Get a Free Assessment", href: "/wildfire-sprinkler#quote" },
  },
  {
    slug: "general-contracting",
    label: "General Contracting",
    shortLabel: "Contracting",
    description: "Licensed general contractor for remodels, additions, wildfire rebuilds, and full-scope residential and commercial projects.",
    heroLine: "Licensed General Contractors",
    features: [
      "Home remodels & additions",
      "Wildfire rebuild & fire damage restoration",
      "ADU construction & garage conversions",
      "Structural repairs & retrofitting",
      "Full permit management & inspections",
      "A licensed general contractor coordinating scope, sequence, inspections, and closeout",
    ],
    cta: { label: "Get a Contracting Quote", href: "/get-quote" },
  },
  {
    slug: "smart-home",
    label: "Smart Home",
    shortLabel: "Smart Home",
    description: "Professional smart home installation — lighting control, security cameras, networking, and climate automation.",
    heroLine: "Smart Home Installation",
    features: [
      "Lutron & Caseta lighting control",
      "Hardwired POE security cameras & NVR",
      "Ubiquiti & Eero whole-home networking",
      "Smart thermostat installation",
      "Structured wiring & A/V systems",
      "Integration with solar & battery systems",
    ],
    cta: { label: "Get a Smart Home Quote", href: "/get-quote" },
  },
];

/* ── Helper to make slugs ── */
function toSlug(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // remove diacritics
    .replace(/'/g, "")
    .replace(/\s+/g, "-");
}

function makeLocations(
  cities: string[],
  unincorporated: string[],
  neighborhoods: string[]
): LocationData[] {
  return [
    ...cities.sort().map((n) => ({ slug: toSlug(n), name: n, type: "city" as const })),
    ...unincorporated.sort().map((n) => ({ slug: toSlug(n), name: n, type: "unincorporated" as const })),
    ...neighborhoods.sort().map((n) => ({ slug: toSlug(n), name: n, type: "neighborhood" as const })),
  ];
}

/* ── Counties ── */
export const COUNTIES: CountyData[] = [
  {
    name: "Los Angeles",
    cityCount: 88,
    locations: makeLocations(
      [
        "Agoura Hills","Alhambra","Arcadia","Artesia","Azusa","Baldwin Park","Bell","Bell Gardens",
        "Bellflower","Beverly Hills","Bradbury","Burbank","Calabasas","Carson","Cerritos","Claremont",
        "Commerce","Compton","Covina","Cudahy","Culver City","Diamond Bar","Downey","Duarte",
        "El Monte","El Segundo","Gardena","Glendale","Glendora","Hawaiian Gardens","Hawthorne",
        "Hermosa Beach","Hidden Hills","Huntington Park","Industry","Inglewood","Irwindale",
        "La Cañada Flintridge","La Habra Heights","La Mirada","La Puente","La Verne","Lakewood",
        "Lancaster","Lawndale","Lomita","Long Beach","Los Angeles","Lynwood","Malibu",
        "Manhattan Beach","Maywood","Monrovia","Montebello","Monterey Park","Norwalk","Palmdale",
        "Palos Verdes Estates","Paramount","Pasadena","Pico Rivera","Pomona","Rancho Palos Verdes",
        "Redondo Beach","Rolling Hills","Rolling Hills Estates","Rosemead","San Dimas","San Fernando",
        "San Gabriel","San Marino","Santa Clarita","Santa Fe Springs","Santa Monica","Sierra Madre",
        "Signal Hill","South El Monte","South Gate","South Pasadena","Temple City","Torrance",
        "Vernon","Walnut","West Covina","West Hollywood","Westlake Village","Whittier",
      ],
      [
        "Acton","Agua Dulce","Altadena","Avocado Heights","Charter Oak","East Los Angeles",
        "East Pasadena","East San Gabriel","Florence-Graham","Hacienda Heights",
        "La Crescenta-Montrose","Ladera Heights","Lennox","Marina del Rey","North El Monte",
        "Quartz Hill","Rowland Heights","South San Gabriel","South San Jose Hills",
        "Stevenson Ranch","Val Verde","View Park-Windsor Hills","West Carson",
        "West Puente Valley","West Rancho Dominguez",
      ],
      [
        "Baldwin Hills","Boyle Heights","Brentwood","Canoga Park","Castaic","Century City",
        "Chatsworth","Cheviot Hills","Eagle Rock","Encino","Granada Hills","Harbor City",
        "Highland Park","Hollywood","Koreatown","Lake Balboa","Leimert Park","Lincoln Heights",
        "Los Feliz","Mar Vista","Mission Hills","Montrose","North Hills","North Hollywood",
        "Pacific Palisades","Panorama City","Playa del Rey","Playa Vista","Reseda","San Pedro",
        "Sherman Oaks","Silver Lake","Studio City","Sun Valley","Sylmar","Tarzana","Toluca Lake",
        "Topanga","Tujunga","Valencia","Valley Village","Van Nuys","Venice","West Hills","Westchester",
        "Westwood","Wilmington","Winnetka","Woodland Hills",
      ]
    ),
  },
  {
    name: "Orange",
    cityCount: 34,
    locations: makeLocations(
      [
        "Aliso Viejo","Anaheim","Brea","Buena Park","Costa Mesa","Cypress","Dana Point",
        "Fountain Valley","Fullerton","Garden Grove","Huntington Beach","Irvine","La Habra",
        "La Palma","Laguna Beach","Laguna Hills","Laguna Niguel","Laguna Woods","Lake Forest",
        "Los Alamitos","Mission Viejo","Newport Beach","Orange","Placentia",
        "Rancho Santa Margarita","San Clemente","San Juan Capistrano","Santa Ana","Seal Beach",
        "Stanton","Tustin","Villa Park","Westminster","Yorba Linda",
      ],
      ["Corona del Mar","Coto de Caza","Ladera Ranch","North Tustin","Rossmoor","Silverado Canyon","Trabuco Canyon"],
      []
    ),
  },
  {
    name: "Riverside",
    cityCount: 28,
    locations: makeLocations(
      [
        "Banning","Beaumont","Blythe","Calimesa","Canyon Lake","Cathedral City","Coachella",
        "Corona","Desert Hot Springs","Eastvale","Hemet","Indian Wells","Indio","Jurupa Valley",
        "La Quinta","Lake Elsinore","Menifee","Moreno Valley","Murrieta","Norco","Palm Desert",
        "Palm Springs","Perris","Rancho Mirage","Riverside","San Jacinto","Temecula","Wildomar",
      ],
      [
        "Anza","French Valley","Good Hope","Highgrove","Homeland","Lakeland Village","Mead Valley",
        "Nuevo","Romoland","Woodcrest",
      ],
      []
    ),
  },
  {
    name: "San Bernardino",
    cityCount: 24,
    locations: makeLocations(
      [
        "Adelanto","Apple Valley","Barstow","Big Bear City","Big Bear Lake","Chino","Chino Hills","Colton",
        "Fontana","Grand Terrace","Hesperia","Highland","Loma Linda","Montclair","Needles",
        "Ontario","Rancho Cucamonga","Redlands","Rialto","San Bernardino","Twentynine Palms",
        "Upland","Victorville","Yucaipa","Yucca Valley",
      ],
      ["Bloomington","Crestline","Joshua Tree","Lake Arrowhead","Lucerne Valley","Mentone","Muscoy","Oak Hills","Phelan","Sugarloaf","Twin Peaks"],
      []
    ),
  },
  {
    name: "Ventura",
    cityCount: 10,
    locations: makeLocations(
      [
        "Camarillo","Fillmore","Moorpark","Ojai","Oxnard","Port Hueneme","Santa Paula",
        "Simi Valley","Thousand Oaks","Ventura",
      ],
      ["Channel Islands","El Rio","Newbury Park","Oak Park","Somis"],
      []
    ),
  },
];

/* ── Flat list for CityServicePage lookups ── */
export type CityData = {
  slug: string;
  name: string;
  county: string;
  snippet: string;
};

// Build a flat CITIES array from COUNTIES for backward compatibility with CityServicePage
export const CITIES: CityData[] = COUNTIES.flatMap((county) =>
  county.locations.map((loc) => ({
    slug: loc.slug,
    name: loc.name,
    county: county.name,
    snippet: `Serving ${loc.name} in ${county.name} County with licensed roofing, solar, electrical, and energy services.`,
  }))
);
